<template>
  <DefaultCrudView
    @switch-inactive="switchInactive"
    @create-new="newGroup"
    :breadcrumbsItems="breadcrumbs"
    :actions="availableActions"
    :inactiveFilterValue="inactiveFilterValue"
    showInactiveFilter
    containerMedium
  >
    <template slot="content">
      <v-card
        v-for="tenant in tenants"
        :key="tenant.tenant_id"
        elevation="4"
        style="margin-bottom:15px"
      >
        <v-card-text>
          <v-treeview
            item-disabled="disabled"
            :items="companiesTree[tenant.tenant_id]"
            :load-children="loadChildren"
            open-on-click
            transition
          >
            <template v-slot:prepend="{ item }">
              <v-icon :class="item.type == 'group' && item.disabled ? 'inactive-row' : ''">
                {{ item.icon }}
              </v-icon>
            </template>

            <template v-slot:label="{ item, open }">
              <div v-if="item.type == 'company'" class="company-label">
                <span>{{ item.name }}</span>
                <div v-if="open" class="group-filter" @click.stop @mousedown.stop @keydown.stop>
                  <label :for="`group-filter-${item.id}`">{{ $t('group_filter') }}:</label>
                  <v-text-field
                    :id="`group-filter-${item.id}`"
                    v-model="item.groupFilter"
                    :aria-label="$t('group_filter')"
                    @input="filterCompanyGroups(item)"
                    clearable
                    dense
                    outlined
                    hide-details
                  />
                </div>
              </div>
              <span v-else>{{ item.name }}</span>
            </template>

            <template v-slot:append="{ item }">
              <template v-if="item.type == 'group'">

                <v-tooltip left>
                  <template v-slot:activator="{ on, attrs }">
                    <v-btn
                      v-if="!item.data.external_group && isCompanyAdmin(item)"
                      v-bind="attrs"
                      v-on="on"
                      icon
                      @click="editGroup(item)"
                    >
                      <v-icon :class="item.type == 'group' && item.disabled ? 'inactive-row' : ''">
                        mdi-pencil
                      </v-icon>
                    </v-btn>
                  </template>
                  <span>{{ $t('edit') }}</span>
                </v-tooltip>

                <v-tooltip top>
                  <template v-slot:activator="{ on, attrs }">
                    <v-btn
                      v-bind="attrs"
                      v-on="on"
                      icon
                      @click="showGroupUsers(item)"
                    >
                      <v-icon :class="item.type == 'group' && item.disabled ? 'inactive-row' : ''">
                        mdi-account-details
                      </v-icon>
                    </v-btn>
                  </template>
                  <span>{{ $t('group_users') }}</span>
                </v-tooltip>

                <v-tooltip right>
                  <template v-slot:activator="{ on, attrs }">
                    <v-btn
                      v-if="item.data.external_group && isCompanyAdmin(item)"
                      v-bind="attrs"
                      v-on="on"
                      icon
                      @click="promptRemoveGroupAccess(item)"
                    >
                      <v-icon :class="item.type == 'group' && item.disabled ? 'inactive-row' : ''">
                        mdi-home-remove
                      </v-icon>
                    </v-btn>
                  </template>
                  <span>{{ $t('remove') }}</span>
                </v-tooltip>

                <v-tooltip right>
                  <template v-slot:activator="{ on, attrs }">
                    <v-btn
                      v-if="!item.data.external_group"
                      v-bind="attrs"
                      v-on="on"
                      icon
                      @click="showExternalCompanies(item)"
                    >
                      <v-icon :class="item.type == 'group' && item.disabled ? 'inactive-row' : ''">
                        mdi-home-export-outline
                      </v-icon>
                    </v-btn>
                  </template>
                  <span>{{ $t('external_companies') }}</span>
                </v-tooltip>

                <v-tooltip right>
                  <template v-slot:activator="{ on, attrs }">
                    <v-btn
                      v-if="isCompanyAdmin(item)"
                      v-bind="attrs"
                      v-on="on"
                      icon
                      @click="openSetSecurityClearanceModal(item)"
                    >
                      <v-icon :class="item.type == 'group' && item.disabled ? 'inactive-row' : ''">
                        mdi-security
                      </v-icon>
                    </v-btn>
                  </template>
                  <span>{{ $t('Security clearance') }}</span>
                </v-tooltip>

              </template>
            </template>

          </v-treeview>
        </v-card-text>
      </v-card>

      <DefaultModal
        :title="getModalTitle()"
        ref="modal"
        v-on:submit="save"
        v-on:modal-closed="resetSelectedGroup"
      >
        <template v-slot:content>
          <DynamicFormContent
            :fields="fields"
            v-on:tenant-change="handleSelectedTenant"
          ></DynamicFormContent>
        </template>
      </DefaultModal>

      <DefaultModal
        :title="$t('Remove Access to Group')"
        ref="removeExternalGroupModal"
        @submit="removeExternalGroup"
        submit-button-text="remove"
        deleteModal
      >
        <template v-slot:content>
          {{ $t('Are you sure you want to remove access to the group') }} <b>{{ selectedGroupName }}</b> ?
        </template>
      </DefaultModal>

      <SecurityClearanceModal ref="securityClearanceModal"></SecurityClearanceModal>

    </template>

  </DefaultCrudView>

</template>

<style scoped>
  .container {
    padding: 0;
  }

  .table-container {
    margin-top: 20px;
  }

  .company-label,
  .group-filter {
    display: flex;
    align-items: center;
    gap: 8px;
  }

  .company-label {
    flex-wrap: wrap;
  }

  .group-filter {
    width: 240px;
    max-width: 100%;
    margin-left: auto;
  }

  .group-filter .v-input {
    min-width: 0;
  }

  .group-filter label {
    color: #757575;
  }

  ::v-deep .inactive-row {
    color: #616161;
    opacity: 0.6;
  }
</style>

<script>

import { mapActions, mapGetters, mapMutations, mapState } from 'vuex'

import router from '@/router'

import DefaultCrudView from '@/components/DefaultCrudView.vue'
import DefaultModal from '@/components/DefaultModal.vue'
import DynamicFormContent from '@/components/DynamicFormContent.vue'
import SecurityClearanceModal from '@/components/node/SecurityClearanceModal.vue'

import TenantActions from '@/store/core/tenant/actions-types'
import ApplicationInstanceActions from '@/store/core/application_instance/actions-types'
import GroupActions from '@/store/core/group/actions-types'
import GroupMutations from '@/store/core/group/mutations-types'
import CompanyActions from '@/store/core/company/actions-types'
import AppActions from '@/store/app/actions-types'
import ThemeActions from '@/store/core/theme/actions-types'
import UserRoles from '@/store/core/user/user-roles'

export default {
  name: "Group",

  components: {
    DefaultCrudView,
    DefaultModal,
    DynamicFormContent,
    SecurityClearanceModal
  },

  computed: {
    ...mapGetters('app', [
      'applicationTitleById',
      'getIconForCoreSettingsItem'
    ]),

    ...mapGetters('core/auth', [
      'checkIsAdminInTenant',
      'checkAccessRights'
    ]),

    ...mapState({
      fields: state => state.core.group.fields,
      headers: state => state.core.group.tableHeaders,
      selectedGroup: state => state.core.group.selectedGroup,
      selectedTenant: state => state.core.group.selectedTenant,
      inactiveFilterValue: state => state.core.group.inactiveFilterValue,
      userRoles: state => state.core.auth.userRoles,
    }),

    breadcrumbs() {
      return [
        {
          text: this.$t('groups'),
          prependIcon: this.getIconForCoreSettingsItem('groups'),
        },
      ]
    },

    selectedGroupName() {
      return this.selectedGroup?.data?.group_title ?? ''
    },

    selectedThemeId() {
      let themes = this.fields.filter(field => field.name == 'theme')
      if (themes.length > 0) {
        return themes[0].value
      }

      return null
    },

    availableActions() {
      return this.actions.filter(action => this.userRoles.filter(role => action.roles.includes(role)).length > 0)
    }
  },

  data() {
    return {
      loading: false,
      groups: [],
      itemsLength: 0,
      tenants: [],
      companiesTree: [],
      actions: [
        {
          text: "create_group",
          eventToEmit: "create-new",
          roles: [UserRoles.USER_ADMIN]
        },
      ]
    }
  },

  mounted() {
    this.loading = true
    let promises = []

    if (this.checkAccessRights([UserRoles.USER_ADMIN, UserRoles.USER_MASTER])) {
      promises.push(this[ApplicationInstanceActions.GET_ALL_APPLICATION_INSTANCE]())
    }

    promises.push(this[GroupMutations.SET_SELECTED_GROUP](null))
    promises.push(this[TenantActions.GET_ALL_TENANTS]()
      .then((tenants) => {
        this.setTenants(tenants.items)
        this[GroupMutations.SET_FIELD_TENANT](tenants.items)
      }))

    Promise.all(promises)
      .finally(() => this.loading = false)
  },

  methods: {
    ...mapActions('core/tenant', [
      TenantActions.GET_ALL_TENANTS
    ]),

    ...mapActions('core/application_instance', [
      ApplicationInstanceActions.GET_ALL_APPLICATION_INSTANCE,
    ]),

    ...mapActions('core/group', [
      GroupActions.SAVE_GROUP,
      GroupActions.HANDLE_APPLICATIONS_ITEMS,
      GroupActions.GET_GROUPS_BY_COMPANY
    ]),

    ...mapActions('core/company', [
      CompanyActions.GET_COMPANIES_BY_TENANT,
      CompanyActions.DELETE_COMPANY_GROUP_RELATIONSHIP,
    ]),

    ...mapActions('app', [
      AppActions.OPEN_APP_SUCCESS_MESSAGE
    ]),

    ...mapActions('core/theme', [
      ThemeActions.GET_TENANT_AVAILABLE_THEMES,
      ThemeActions.LINK_THEME_TO_GROUP,
      ThemeActions.UNLINK_THEME_TO_GROUP,
    ]),

    ...mapMutations('core/group', [
      GroupMutations.SET_FIELD_TENANT,
      GroupMutations.SET_FIELD_COMPANY,
      GroupMutations.SET_SELECTED_GROUP,
      GroupMutations.SET_SELECTED_TENANT,
      GroupMutations.SET_LAST_SEARCH_TENANTS,
      GroupMutations.SET_LAST_SEARCH_COMPANIES,
      GroupMutations.SET_INACTIVE_FILTER_VAL,
      GroupMutations.SET_THEME_ITEMS
    ]),

    setTenants(items) {
      let companiesTree = []

      items.forEach(item => {
        companiesTree[item.tenant_id] = [
          {id: `tenant-${item.tenant_id}`, type: 'tenant', name: item.title, icon: 'mdi-home-account', children: [], data: item}
        ]
      })

      this.tenants = items
      this.companiesTree = companiesTree
      this[GroupMutations.SET_LAST_SEARCH_TENANTS](items)
    },

    async loadChildren(item) {
      if (item.type == 'tenant') {
        return this[CompanyActions.GET_COMPANIES_BY_TENANT](item.data.tenant_id)
          .then((result) => {
            let companies = []
            result.items.forEach(
              company => companies.push(
                {
                  id: `company-${company.company_id}`,
                  name: company.name,
                  type: 'company',
                  icon: 'mdi-office-building',
                  children: [],
                  groups: [],
                  groupFilter: '',
                  data: company,
                  origin_tenant: item.data.tenant_id
                }
              )
            )

            this[GroupMutations.SET_LAST_SEARCH_COMPANIES](result.items)

            return companies
          })
          .then(companies => (item.children.push(...companies)))
      }

      if (item.type == 'company') {
        let options = {...this.$route.query}

        if (!this.inactiveFilterValue) {
          options = {
            ...options,
            'filter[active]' : "true"
          }
        }

        let params = {companyId: item.data.company_id, options: options}

        return this[GroupActions.GET_GROUPS_BY_COMPANY](params)
          .then((result) => {
            let groups = []
            result.items.forEach(
              group => groups.push(
                {
                  id: group.user_group_id,
                  name: (group.external_group ? `(${group.company_owner}) ` : '') + group.group_title,
                  type: 'group',
                  icon: group.external_group ? 'mdi-account-group' : 'mdi-account-multiple',
                  disabled: !group.active,
                  data: {
                    ...group,
                    active: group.active !== undefined ? group.active : true
                  },
                  origin_company: item.data,
                  origin_tenant: item.origin_tenant
                }
              )
            )

            return groups
          })
          .then(groups => {
            item.groups = groups
            this.filterCompanyGroups(item)
          })
      }
    },

    filterCompanyGroups(company) {
      const normalize = value => (value || '')
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .toLocaleLowerCase()
      const search = normalize(company.groupFilter)
      company.children = company.groups
        .filter(group => normalize(group.data.group_title).includes(search))
        .sort((a, b) => a.data.group_title.localeCompare(b.data.group_title, 'pt-BR', {
          sensitivity: 'base'
        }))
    },

    newGroup() {
      if (this.selectedGroup) {
        this[GroupMutations.SET_SELECTED_GROUP](null)
      }

      this[GroupMutations.SET_THEME_ITEMS]([])
      this.$refs.modal.openModal()
    },

    save() {
      this.$refs.modal.errorMessage = null
      this.$refs.modal.errorList = []

      this[GroupActions.SAVE_GROUP]()
        .then((result) => {
          return this.setGroupTheme(result)
        })
        .then((message) => {
          this[AppActions.OPEN_APP_SUCCESS_MESSAGE](this.$t(message))

          this.reloadData()
          this.$refs.modal.closeModal()

          this[GroupMutations.SET_SELECTED_GROUP](null)
        })
        .finally(() => this.$refs.modal.submitting = false)
    },

    setGroupTheme(groupData) {
      let message = this.selectedGroup ? 'Group edited' : 'Group created'
      const userGroupId = this.selectedGroup ? this.selectedGroup.user_group_id : groupData.user_group_id

      const currentGroupThemeId = this.selectedGroup?.theme_id

      if (! currentGroupThemeId && ! this.selectedThemeId) {
        return message
      }

      let themeLinkEndpoint = ThemeActions.LINK_THEME_TO_GROUP
      let themeId = this.selectedThemeId

      if (! this.selectedThemeId) {
        themeLinkEndpoint = ThemeActions.UNLINK_THEME_TO_GROUP
        themeId = currentGroupThemeId
      }

      this[themeLinkEndpoint]({groupId: userGroupId, themeId})

      return message
    },

    reloadData(ignoreSelectedField) {
      this.companiesTree.forEach(tenants => {
        tenants.forEach(company => {
          company.children.forEach(company => {
            if (!this.selectedGroup) {
              let companyField = this.fields.find(field => { return field.name == 'company_id'})
              if (ignoreSelectedField || company.data.company_id == companyField.value) {
                this.loadChildren(company)
              }
            }

            if (this.selectedGroup) {
              company.groups.forEach(group => {
                if (group.id == this.selectedGroup.user_group_id) {
                  group.name = this.fields.find(field => { return field.name == 'group_title' }).value
                  group.disabled = !this.fields.find(field => { return field.name == 'active' }).value
                  group.data.group_title = this.fields.find(field => { return field.name == 'group_title' }).value
                  group.data.active = this.fields.find(field => { return field.name == 'active' }).value
                  group.data.application_ids = this.fields.find(field => { return field.name == 'application_ids' }).value
                  group.data.application_instance_ids = this.fields.find(field => { return field.name == 'application_instance_ids' }).value
                  group.data.theme_id = this.fields.find(field => { return field.name == 'theme'}).value
                }
              })
              this.filterCompanyGroups(company)
            }
          })
        })
      })
    },

    editGroup(item) {
      // Ensure we have the most up-to-date group data
      const groupData = {
        ...item.data,
        active: item.data.active !== undefined ? item.data.active : !item.disabled
      }
      
      this[GroupMutations.SET_SELECTED_GROUP](groupData)

      let tenant = this.tenants.find(f => {return f.tenant_id == item.data.tenant_id})

      if (tenant) {
        this[GroupActions.HANDLE_APPLICATIONS_ITEMS]({
          applicationsIds: tenant.application_ids,
          applicationsInstanceIds: tenant.app_instance_ids
        })

        this[ThemeActions.GET_TENANT_AVAILABLE_THEMES](tenant.tenant_id)
          .then((result) => {
              this[GroupMutations.SET_THEME_ITEMS](result.items)
          })
      }

      this.$refs.modal.openModal()
    },

    promptRemoveGroupAccess(item) {
      this[GroupMutations.SET_SELECTED_GROUP](item)

      this.$refs.removeExternalGroupModal.openModal()
    },

    removeExternalGroup() {
      const companyId = this.selectedGroup.origin_company.company_id
      const userGroupId = this.selectedGroup.id

      this[CompanyActions.DELETE_COMPANY_GROUP_RELATIONSHIP]({companyId, userGroupId})
        .then(() => {
          this[AppActions.OPEN_APP_SUCCESS_MESSAGE](this.$t('Access to group removed!'))

          this.$refs.removeExternalGroupModal.closeModal()

          this[GroupMutations.SET_SELECTED_GROUP](null)

          this.reloadData(true)
        })
        .catch(() => this.$refs.removeExternalGroupModal.submitting = false)
    },

    showGroupUsers(item) {
      this[GroupMutations.SET_SELECTED_GROUP](item.data)

      let params = {groupId: item.id, companyId: item.origin_company.company_id}

      if (item.data.external_group) {
        params.originTenant = item.origin_tenant
      }

      router.push({name: 'GroupUsers', params: params})
    },

    showExternalCompanies(item) {
      router.push({name: 'ExternalCompanies', params: {groupId: item.id}})
    },

    getModalTitle() {
      return this.selectedGroup ? this.$t('Edit Group #' + this.selectedGroup.user_group_id) : this.$t('New Group')
    },

    switchInactive() {
      this[GroupMutations.SET_INACTIVE_FILTER_VAL](!this.inactiveFilterValue)
      this.reloadData(true)
    },

    handleSelectedTenant () {
      let tenantField = this.fields.find(f => f.name == 'tenant_id')

      let selectedTenant = null

      if (tenantField.value) {
        selectedTenant = tenantField.items.find(f => f.tenant_id == tenantField.value)
      }

      if (selectedTenant == this.selectedTenant) {
        return
      }

      this[GroupMutations.SET_SELECTED_TENANT](selectedTenant)

      if (selectedTenant) {
        this.$refs.modal.submitting = true

        this[CompanyActions.GET_COMPANIES_BY_TENANT](selectedTenant.tenant_id)
          .then((result) => {
            this[GroupMutations.SET_FIELD_COMPANY](result.items)

            this[GroupActions.HANDLE_APPLICATIONS_ITEMS]({
              applicationsIds: selectedTenant.application_ids,
              applicationsInstanceIds: selectedTenant.app_instance_ids
            })

            this.$refs.modal.submitting = false
          })
          .catch(() => this.$refs.modal.submitting = false)

        this[ThemeActions.GET_TENANT_AVAILABLE_THEMES](selectedTenant.tenant_id)
          .then((result) => {
              this[GroupMutations.SET_THEME_ITEMS](result.items)
          })

        return
      }

      this[GroupMutations.SET_THEME_ITEMS]([])

      this[GroupMutations.SET_FIELD_TENANT]([])
      this[GroupMutations.SET_FIELD_COMPANY]([])
      this[GroupActions.HANDLE_APPLICATIONS_ITEMS]({
        applicationsIds: [],
        applicationsInstanceIds: []
      })
    },

    resetSelectedGroup() {
      // Let the modal's form reset reach the inputs before restoring defaults.
      this.$nextTick(() => {
        this[GroupMutations.SET_SELECTED_GROUP](null)
      })
    },

    isCompanyAdmin(item) {
      const tenantId = item.origin_tenant

      return this.checkIsAdminInTenant(tenantId)
    },

    openSetSecurityClearanceModal(item) {
      const tenantId = item.origin_tenant

      this.$refs.securityClearanceModal.open(item.id, item.name, tenantId)
    }
  }
}

</script>
